import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import { PencilSquare, PlusCircle } from "react-bootstrap-icons";
import { Modal, Button, Alert } from "react-bootstrap";
import API_CONFIG, { buildApiUrl } from '../config/api';

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    first_name: "",
    last_name: "",
    current_rank: "",
    is_junior: "all",
    active: "true",
    has_medical_conditions: "all",
    has_allergies: "all"
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    const fetchStudents = async () => {
      const token = localStorage.getItem("token"); // Retrieve token

      if (!token) {
        console.error("No authentication token found.");
        return;
      } else {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        setGroups(decoded["cognito:groups"]);
      }

      try {
        const response = await fetch(
          buildApiUrl(API_CONFIG.ENDPOINTS.STUDENTS),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch students");
        }
        const data = await response.json();
        console.log(data);
        setStudents(data);
        setFilteredStudents(data);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    const filtered = students.filter(student => {
      const matchesFirstName = (student.first_name || "").toLowerCase().includes(searchFilters.first_name.toLowerCase()) ||
        (student.nick_name && student.nick_name.toLowerCase().includes(searchFilters.first_name.toLowerCase()));
      const matchesLastName = (student.last_name || "").toLowerCase().includes(searchFilters.last_name.toLowerCase());
      const matchesRank = (student.current_rank || "").toLowerCase().includes(searchFilters.current_rank.toLowerCase());
      const matchesJunior = searchFilters.is_junior === "all" || 
        (searchFilters.is_junior === "yes" && student.is_junior) || 
        (searchFilters.is_junior === "no" && !student.is_junior);
      const matchesActive = searchFilters.active === "all" || 
        (searchFilters.active === "true" && student.active) || 
        (searchFilters.active === "false" && !student.active);
      const matchesMedical = searchFilters.has_medical_conditions === "all" || 
        (searchFilters.has_medical_conditions === "yes" && student.has_medical_conditions === "*") || 
        (searchFilters.has_medical_conditions === "no" && student.has_medical_conditions !== "*");
      const matchesAllergies = searchFilters.has_allergies === "all" || 
        (searchFilters.has_allergies === "yes" && student.has_allergies === "*") || 
        (searchFilters.has_allergies === "no" && student.has_allergies !== "*");

      return matchesFirstName && matchesLastName && matchesRank && matchesJunior && matchesActive && matchesMedical && matchesAllergies;
    });

    setFilteredStudents(filtered);
  }, [searchFilters, students]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    // Helper function to parse CSV line handling quotes
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const students = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const student = {};
      
      headers.forEach((header, index) => {
        student[header] = values[index] || '';
      });
      
      if (student.student_first_name && student.last_name) {
        students.push(student);
      }
    }

    return students;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadStatus({ type: '', message: '' });

    try {
      const text = await file.text();
      const students = parseCSV(text);

      if (students.length === 0) {
        setUploadStatus({ type: 'danger', message: 'No valid students found in CSV file' });
        return;
      }

      // Call the API to upload
      const token = localStorage.getItem("token");
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.BULK_UPSERT_STUDENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ students })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();
      setUploadStatus({ 
        type: 'success', 
        message: `Successfully uploaded ${result.students_processed} students and ${result.guardians_processed} guardians. ${result.errors.length} errors.` 
      });

      // Refresh the student list
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadStatus({ type: 'danger', message: error.message || 'Failed to upload CSV file' });
    }

    // Reset file input
    event.target.value = '';
  };

  if (loading) return <p className="text-center text-light">Loading...</p>;

  return (
    <div className="container mt-4">
      <Navbar groups={groups} />
      <div className="card p-3 shadow-lg">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="text-primary mb-0">Student List</h2>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-info"
              onClick={() => setShowUploadModal(true)}
            >
              Upload CSV
            </button>
            <Link to="/students/new" className="btn btn-success">
              <PlusCircle className="me-2" />
              New Student
            </Link>
          </div>
        </div>

        {/* Upload CSV Modal */}
        <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Upload Students from CSV</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {uploadStatus.message && (
              <Alert variant={uploadStatus.type || 'info'}>
                {uploadStatus.message}
              </Alert>
            )}
            <div className="mb-3">
              <p>Upload a CSV file with student information. The CSV should have the following columns:</p>
              <small className="text-muted d-block mb-3">
                student_first_name, last_name, nick_name, date_of_birth, rank, stripes, date_promoted, 
                start_date, tuition_amount, tuition_due_day, phone, email, street_1, street_2, city, state, zip,
                medical_conditions, allergies, guardian_1_first_name, guardian_1_last_name, 
                guardian_1_relationship, guardian_1_email, guardian_1_phone_number, guardian_1_financial_email_notify,
                guardian_2_first_name, guardian_2_last_name, guardian_2_relationship, guardian_2_email, 
                guardian_2_phone_number, guardian_2_financial_email_notify
              </small>
              <div className="mb-3">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  href="/new_students_upload_sample.csv" 
                  download
                  onClick={(e) => {
                    // Create a temporary link to trigger download
                    const link = document.createElement('a');
                    link.href = '/new_students_upload_sample.csv';
                    link.download = 'new_students_upload_sample.csv';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <i className="bi bi-download me-2"></i>
                  Download Sample CSV Template
                </Button>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="csv-upload" className="form-label fw-bold">Select CSV File:</label>
              <input
                id="csv-upload"
                type="file"
                className="form-control"
                accept=".csv"
                onChange={handleFileUpload}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Search Filters */}
        <div className="card bg-dark mb-4">
          <div className="card-body">
            <h5 className="card-title text-light mb-3">Search Filters</h5>
            <div className="row g-3">
              <div className="col-md-3">
                <input
                  type="text"
                  name="first_name"
                  value={searchFilters.first_name}
                  onChange={handleSearchChange}
                  className="form-control"
                  placeholder="First Name"
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  name="last_name"
                  value={searchFilters.last_name}
                  onChange={handleSearchChange}
                  className="form-control"
                  placeholder="Last Name"
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  name="current_rank"
                  value={searchFilters.current_rank}
                  onChange={handleSearchChange}
                  className="form-control"
                  placeholder="Rank"
                />
              </div>
              <div className="col-md-3">
                <select
                  name="is_junior"
                  value={searchFilters.is_junior}
                  onChange={handleSearchChange}
                  className="form-control"
                >
                  <option value="all">All Students</option>
                  <option value="yes">Junior Students</option>
                  <option value="no">Adult Students</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  name="active"
                  value={searchFilters.active}
                  onChange={handleSearchChange}
                  className="form-control"
                >
                  <option value="true">Active Students</option>
                  <option value="false">Inactive Students</option>
                  <option value="all">All Students</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  name="has_medical_conditions"
                  value={searchFilters.has_medical_conditions}
                  onChange={handleSearchChange}
                  className="form-control"
                >
                  <option value="all">All Medical Conditions</option>
                  <option value="yes">Has Medical Conditions</option>
                  <option value="no">No Medical Conditions</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  name="has_allergies"
                  value={searchFilters.has_allergies}
                  onChange={handleSearchChange}
                  className="form-control"
                >
                  <option value="all">All Allergies</option>
                  <option value="yes">Has Allergies</option>
                  <option value="no">No Allergies</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-dark table-hover table-bordered">
            <thead className="thead-light">
              <tr>
                <th>ID</th>
                <th>Active</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>DOB</th>
                <th>Phone Number</th>
                <th>Email</th>
                <th>Current Rank</th>
                <th>Current Stripes</th>
                <th>Is Junior</th>
                <th>Date of Last Promotion</th>
                <th>Medical Conditions</th>
                <th>Allergies</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr key={student.student_id}>
                  <td>{student.student_id}</td>
                  <td>{student.active ? "YES" : "NO"}</td>
                  <td>{student.nick_name || student.first_name}</td>
                  <td>{student.last_name}</td>
                  <td>{student.date_of_birth}</td>
                  <td>{student.phone}</td>
                  <td>{student.email}</td>
                  <td>{student.current_rank}</td>
                  <td>{student.current_stripes}</td>
                  <td>{student.is_junior ? "YES" : "NO"}</td>
                  <td>{student.date_last_promoted}</td>
                  <td>{student.has_medical_conditions}</td>
                  <td>{student.has_allergies}</td>
                  <td className="text-center">
                    <Link to={`/students/edit/${student.student_id}`} className="text-warning">
                      <PencilSquare size={20} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentList;
